import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../components/site/Navbar';
import Footer from '../components/site/Footer';
import ChatWidget from '../components/site/ChatWidget';
import { Calendar } from '../components/ui/calendar';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { bookMeeting, fetchMeetingSlots, detectGeo } from '../lib/api';
import { nearestOffice } from '../lib/offices';
import { serviceOptions } from '../mock';
import { useI18n } from '../i18n';
import Seo from '../components/Seo';
import { ArrowLeft, CalendarCheck, Clock, Loader2, Download, CheckCircle2, Globe2, MapPin } from 'lucide-react';

const TIMEZONES = [
  { value: 'IST', label: 'IST (India · UTC+05:30)', offset: 5.5 },
  { value: 'GST', label: 'GST (UAE · UTC+04:00)', offset: 4 },
  { value: 'GMT', label: 'GMT (UK · UTC+00:00)', offset: 0 },
  { value: 'CET', label: 'CET (Europe · UTC+01:00)', offset: 1 },
  { value: 'EST', label: 'EST (US East · UTC−05:00)', offset: -5 },
  { value: 'PST', label: 'PST (US West · UTC−08:00)', offset: -8 },
  { value: 'MYT', label: 'MYT (Malaysia · UTC+08:00)', offset: 8 },
  { value: 'HKT', label: 'HKT (Hong Kong · UTC+08:00)', offset: 8 },
];

const detectBestTz = () => {
  const offH = -new Date().getTimezoneOffset() / 60;
  return TIMEZONES.reduce((b, tz) => (Math.abs(tz.offset - offH) < Math.abs(b.offset - offH) ? tz : b), TIMEZONES[0]).value;
};

const toISODate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const parseSlot12h = (slot) => {
  // "10:00 AM - 10:30 AM" -> [{h,m}, {h,m}]
  const parts = slot.split(' - ').map((s) => {
    const [t, ampm] = s.trim().split(' ');
    let [hh, mm] = t.split(':').map(Number);
    if (ampm === 'PM' && hh !== 12) hh += 12;
    if (ampm === 'AM' && hh === 12) hh = 0;
    return { h: hh, m: mm };
  });
  return parts;
};

const pad = (n) => String(n).padStart(2, '0');

// Convert an IST slot start time to a given fixed-offset timezone
const slotInTimezone = (slotStr, tzValue) => {
  const tz = TIMEZONES.find((t) => t.value === tzValue);
  if (!tz || tz.value === 'IST') return null;
  const [{ h, m }] = parseSlot12h(slotStr);
  let mins = h * 60 + m + Math.round((tz.offset - 5.5) * 60);
  let dayShift = 0;
  if (mins < 0) { mins += 1440; dayShift = -1; }
  if (mins >= 1440) { mins -= 1440; dayShift = 1; }
  const hh = Math.floor(mins / 60);
  const mm = mins % 60;
  const ampm = hh >= 12 ? 'PM' : 'AM';
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return {
    label: `${h12}:${pad(mm)} ${ampm}${dayShift === 1 ? ' (+1d)' : dayShift === -1 ? ' (−1d)' : ''}`,
    good: dayShift === 0 && hh >= 8 && hh < 20,
  };
};

const buildICS = (meeting) => {
  const [s, e] = parseSlot12h(meeting.slot);
  const [y, mo, d] = meeting.date.split('-').map(Number);
  const dt = (h, m) => `${y}${pad(mo)}${pad(d)}T${pad(h)}${pad(m)}00`;
  const uid = `${meeting.id || Date.now()}@hiprotechnologies.com`;
  const stamp = new Date().toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Hipro Technologies//Booking//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${dt(s.h, s.m)}`,
    `DTEND:${dt(e.h, e.m)}`,
    'SUMMARY:Hipro Technologies — Strategy Call',
    `DESCRIPTION:Consultation with Hipro Technologies. Service: ${meeting.service || 'General'}. Notes: ${(meeting.notes || '').replace(/\n/g, ' ')}`,
    'LOCATION:Google Meet (link will be shared via email)',
    'STATUS:CONFIRMED',
    'BEGIN:VALARM',
    'TRIGGER:-PT15M',
    'ACTION:DISPLAY',
    'DESCRIPTION:Reminder',
    'END:VALARM',
    'END:VEVENT',
    'END:VCALENDAR',
  ].join('\r\n');
};

const downloadICS = (meeting) => {
  const content = buildICS(meeting);
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `hipro-meeting-${meeting.date}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const BookMeetingPage = () => {
  const nav = useNavigate();
  const { toast } = useToast();
  const { lang, t } = useI18n();

  // start from tomorrow by default
  const tomorrow = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d;
  }, []);

  const [selectedDate, setSelectedDate] = useState(tomorrow);
  const [slots, setSlots] = useState([]);
  const [slot, setSlot] = useState('');
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [form, setForm] = useState(() => ({ name: '', email: '', phone: '', timezone: detectBestTz(), service: '', notes: '' }));
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [detectedTz, setDetectedTz] = useState('');
  const [nearest, setNearest] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    setDetectedTz(Intl.DateTimeFormat().resolvedOptions().timeZone || '');
    detectGeo().then((pos) => {
      if (pos) {
        const { office } = nearestOffice(pos.lat, pos.lng);
        if (office) setNearest(office);
      }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSlot('');
    fetchMeetingSlots(toISODate(selectedDate))
      .then((res) => setSlots(res.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !slot) {
      toast({ title: t('form.validation'), description: t('form.validationDesc') });
      return;
    }
    setSubmitting(true);
    try {
      const res = await bookMeeting({
        ...form,
        date: toISODate(selectedDate),
        slot,
        lang,
      });
      setConfirmed(res);
      toast({ title: t('book.confirmedToast'), description: t('book.confirmedToastDesc') });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast({ title: t('book.slotTaken'), description: t('book.slotTakenDesc') });
        fetchMeetingSlots(toISODate(selectedDate)).then((r) => setSlots(r.slots || []));
        setSlot('');
      } else {
        const msg = Array.isArray(detail) ? detail.map((d) => d.msg).join(', ') : detail || '';
        toast({ title: t('form.error'), description: msg });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Confirmation view
  if (confirmed) {
    return (
      <div className="min-h-screen bg-white text-slate-900">
        <Navbar />
        <section className="pt-28 pb-16 bg-slate-950 text-white">
          <div className="max-w-2xl mx-auto px-5 lg:px-8 text-center">
            <div className="h-16 w-16 rounded-full bg-emerald-500 text-white flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-8 w-8" />
            </div>
            <h1 className="mt-6 text-3xl sm:text-4xl font-extrabold">{t('book.booked')}</h1>
            <p className="mt-3 text-slate-300">{t('book.bookedBody')}</p>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-2xl mx-auto px-5 lg:px-8">
            <div className="rounded-2xl border border-slate-200 p-6 sm:p-8 bg-white">
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div><div className="text-slate-500">{t('book.fullName').replace(' *','')}</div><div className="font-semibold">{confirmed.name}</div></div>
                <div><div className="text-slate-500">{t('book.workEmail').replace(' *','')}</div><div className="font-semibold">{confirmed.email}</div></div>
                <div><div className="text-slate-500">{t('caseStudy.duration')}</div><div className="font-semibold">{new Date(confirmed.date).toDateString()}</div></div>
                <div><div className="text-slate-500">{t('book.timezone')}</div><div className="font-semibold">{confirmed.slot} ({confirmed.timezone})</div></div>
                {confirmed.service && (<div><div className="text-slate-500">{t('form.selectService').replace('*','')}</div><div className="font-semibold">{confirmed.service}</div></div>)}
                {confirmed.phone && (<div><div className="text-slate-500">{t('book.phone')}</div><div className="font-semibold">{confirmed.phone}</div></div>)}
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button onClick={() => downloadICS(confirmed)} className="bg-orange-500 hover:bg-orange-600 text-white h-11 px-5">
                  <Download className="h-4 w-4 mr-2" /> {t('book.downloadIcs')}
                </Button>
                <Button onClick={() => nav('/')} variant="outline" className="h-11 px-5 border-slate-300">{t('book.backToHome')}</Button>
              </div>
              <p className="mt-4 text-xs text-slate-500">{t('book.reminder')}</p>
            </div>
          </div>
        </section>
        <Footer />
        <ChatWidget />
      </div>
    );
  }

  const isPastDay = (d) => {
    const today = new Date(); today.setHours(0,0,0,0);
    return d < today;
  };

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Seo
        title="Book a Free Consultation | Hipro Technologies"
        description="Schedule a free 30-minute consultation with our experts. Get a detailed project estimate within 24 hours for web, mobile, AI or marketing projects."
        path="/book-meeting"
      />
      <Navbar />

      <section className="pt-28 pb-10 bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-300 hover:text-orange-400">
            <ArrowLeft className="h-4 w-4" /> {t('common.backHome')}
          </Link>
          <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-orange-400">{t('book.kicker')}</span>
              <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-extrabold">{t('book.title')}</h1>
              <p className="mt-3 text-slate-300 max-w-2xl">{t('book.body')}</p>
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-2 border border-white/10 rounded-full px-3 py-1.5">
              <Globe2 className="h-3.5 w-3.5" /> India · USA · UK · UAE · Malaysia · Netherlands · Hong Kong
            </div>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-6xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5">
            <div className="rounded-2xl border border-slate-200 bg-white p-4">
              <div className="px-2 pt-2 pb-3 flex items-center gap-2 text-sm text-slate-700">
                <CalendarCheck className="h-4 w-4 text-orange-500" /> {t('book.pickDate')}
              </div>
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={(d) => d && setSelectedDate(d)}
                disabled={isPastDay}
                className="mx-auto"
              />
            </div>

            <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-center gap-2 text-sm text-slate-700">
              <Clock className="h-4 w-4 text-orange-500" /> {t('book.pickSlot')} {selectedDate ? selectedDate.toDateString() : ''}
            </div>
              {(detectedTz || nearest) && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="tz-detect-banner">
                  {detectedTz && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
                      <Globe2 className="h-3 w-3" /> {t('book.autoTz')}: {detectedTz} → {form.timezone}
                    </span>
                  )}
                  {nearest && (
                    <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 bg-slate-100 border border-slate-200 rounded-full px-2.5 py-1" data-testid="nearest-office-chip">
                      <MapPin className="h-3 w-3 text-orange-500" /> {t('book.nearest')}: {nearest.label}, {nearest.country}
                    </span>
                  )}
                </div>
              )}
              <div className="mt-4 grid grid-cols-2 gap-2">
                {loadingSlots && (
                  <div className="col-span-2 text-center py-6 text-slate-500">
                    <Loader2 className="h-5 w-5 mx-auto animate-spin" /> {t('book.loadingSlots')}
                  </div>
                )}
                {!loadingSlots && slots.map((s) => {
                  const local = slotInTimezone(s.slot, form.timezone);
                  return (
                    <button
                      key={s.slot}
                      type="button"
                      disabled={!s.available}
                      onClick={() => setSlot(s.slot)}
                      data-testid={`slot-btn-${s.slot.replace(/[^a-zA-Z0-9]+/g, '-')}`}
                      className={`px-3 py-2 rounded-lg text-sm border transition-colors text-left relative ${
                        slot === s.slot
                          ? 'bg-orange-500 text-white border-orange-500'
                          : s.available
                            ? 'bg-white border-slate-200 hover:border-orange-400 text-slate-800'
                            : 'bg-slate-100 text-slate-400 border-slate-100 cursor-not-allowed line-through'
                      }`}
                    >
                      <span className="block">{s.slot.split(' - ')[0]}</span>
                      {local && (
                        <span className={`block text-[10px] mt-0.5 ${slot === s.slot ? 'text-orange-100' : local.good ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                          {local.label} {t('book.yourTime')}{local.good && s.available ? ' ✓' : ''}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              <p className="mt-3 text-[11px] text-slate-500">{t('book.slotNote')}</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form onSubmit={submit} className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label>{t('book.fullName')}</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 mt-1" placeholder="Jane Doe" />
                </div>
                <div>
                  <Label>{t('book.workEmail')}</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 mt-1" placeholder="you@company.com" />
                </div>
                <div>
                  <Label>{t('book.phone')}</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 mt-1" placeholder="+1 555 123 4567" />
                </div>
                <div>
                  <Label>{t('book.timezone')}</Label>
                  <Select value={form.timezone} onValueChange={(v) => v && setForm({ ...form, timezone: v })}>
                    <SelectTrigger className="h-11 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((tz) => <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>{t('book.help')}</Label>
                <Select value={form.service} onValueChange={(v) => setForm({ ...form, service: v })}>
                  <SelectTrigger className="h-11 mt-1"><SelectValue placeholder={t('book.helpPh')} /></SelectTrigger>
                  <SelectContent>
                    {serviceOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>{t('book.notes')}</Label>
                <Textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" placeholder={t('book.notesPh')} />
              </div>

              <div className="rounded-xl border border-slate-200 p-4 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm text-slate-700">
                  <div className="text-xs text-slate-500">{t('book.yourBooking')}</div>
                  <div className="font-semibold">
                    {selectedDate ? selectedDate.toDateString() : t('book.pickDate')} · {slot || t('book.pickSlotPlaceholder')} · {form.timezone}
                    {slot && slotInTimezone(slot, form.timezone) && (
                      <span className="block text-xs font-normal text-slate-500 mt-0.5">
                        = {slotInTimezone(slot, form.timezone).label} {t('book.yourTime')} ({form.timezone})
                      </span>
                    )}
                  </div>
                </div>
                <Button type="submit" disabled={submitting || !slot} className="h-11 px-6 bg-orange-500 hover:bg-orange-600 text-white font-semibold">
                  {submitting ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('book.confirming')}</>) : t('book.confirm')}
                </Button>
              </div>

              <p className="text-[11px] text-slate-500">{t('book.disclaimer')}</p>
            </form>
          </div>
        </div>
      </section>

      <Footer />
      <ChatWidget />
    </div>
  );
};

export default BookMeetingPage;
